import classNames from 'classnames';
import { FC } from 'react';

// SEBI deadline after which accreditation becomes mandatory to keep investing.
export const ACCREDITATION_RENEWAL_DATE = new Date('2027-03-31T00:00:00+05:30');

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

type UrgencyTone = 'critical' | 'warning' | 'info';

const TONE_STYLES: Record<UrgencyTone, { wrapper: string; icon: string; secondary: string }> = {
  critical: {
    wrapper: 'border-red-200 bg-red-50 text-red-900 ring-red-100',
    icon: 'text-red-700',
    secondary: 'text-red-700',
  },
  warning: {
    wrapper: 'border-amber-200 bg-amber-50 text-amber-900 ring-amber-100',
    icon: 'text-amber-700',
    secondary: 'text-amber-700',
  },
  info: {
    wrapper: 'border-blue-200 bg-blue-50 text-blue-900 ring-blue-100',
    icon: 'text-blue-700',
    secondary: 'text-blue-700',
  },
};

export const getDaysUntil = (deadline: Date, now: Date = new Date()): number =>
  Math.max(0, Math.ceil((deadline.getTime() - now.getTime()) / MILLISECONDS_PER_DAY));

export const getUrgencyTone = (daysLeft: number): UrgencyTone => {
  if (daysLeft <= 7) return 'critical';
  if (daysLeft <= 30) return 'warning';
  return 'info';
};

type Props = {
  deadline?: Date;
};

const AccreditationDeadlineBanner: FC<Props> = ({ deadline = ACCREDITATION_RENEWAL_DATE }) => {
  const daysLeft = getDaysUntil(deadline);
  const tone = getUrgencyTone(daysLeft);
  const styles = TONE_STYLES[tone];

  const deadlineLabel = deadline.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return (
    <div
      className={classNames(
        'mb-6 flex flex-col gap-3 rounded-xl border px-5 py-4 shadow-sm ring-4 sm:flex-row sm:items-center sm:justify-between',
        styles.wrapper
      )}
      role="status"
      aria-live="polite"
    >
      <div className="flex min-w-0 items-start gap-3">
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={classNames(
            'mt-0.5 h-8 w-8 flex-none',
            // Pulse only in the final week; a permanently animated icon is noise.
            tone === 'critical' && 'animate-pulse',
            styles.icon
          )}
        >
          <circle cx="12" cy="12" r="8" />
          <path d="M12 7v5l3 2" />
        </svg>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">
            {daysLeft === 0
              ? 'Accreditation is now mandatory — get accredited to keep investing.'
              : `Accreditation becomes mandatory in ${daysLeft} day${daysLeft === 1 ? '' : 's'}.`}
          </p>
          <p className={classNames('text-xs font-medium', styles.secondary)}>
            Deadline: {deadlineLabel}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccreditationDeadlineBanner;
